import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  forwardRef,
  OnDestroy,
  ViewChild,
  ChangeDetectionStrategy,
} from "@angular/core";
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from "@angular/forms";
import { AgEditorComponent } from "@bfx/ag-grid-angular";
import { DomHandler } from "@bfx/primeng/dom";
import { ObjectUtils } from "@bfx/primeng/utils";
import { Calendar } from "@bfx/primeng/calendar";
import { ICellEditorParams } from "ag-grid-community";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { Subject, Subscription } from "rxjs";

import { LoggerService } from "@bfx/logger";
import { CurrencyPairCalendarPrimaryLookupService } from "../../../shared/cubiq-services";
const logger = LoggerService.for(__dirname);

@Component({
  selector: "df-calendar",
  templateUrl: "./html/DfCalendar.html",
  host: {
    "[class.df-calendar-ag-cell]": "isMoment",
    "[class.ui-inputwrapper-filled]": "filled",
    "[class.ui-inputwrapper-focus]": "focus",
  },
  providers: [
    DomHandler,
    ObjectUtils,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DfCalendarComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DfCalendarComponent
  implements AgEditorComponent, ControlValueAccessor, AfterViewInit, OnDestroy
{
  @ViewChild("dfCalendar")
  calendar: Calendar;

  @ViewChild("focus")
  focusElement: ElementRef;

  /** is ag-grid value CubiqTS.moment */
  isMoment: boolean = false;

  onChange: any = () => {};
  onTouch: any = () => {};
  private _value: Date;
  public pendingValue: any;
  public pendingParams: ICellEditorParams;
  public params: ICellEditorParams;
  public ready: boolean = false;
  public editingEnabled: boolean;

  public minDate: Date;
  private minDateSubscription: Subscription;

  onCloseSubscription: Subscription;
  onBlurSubscription: Subscription;
  onEditingStoppedSubscription: Subscription;
  textValue: string = "";
  lockedValueDate: any;

  constructor(
    public cd: ChangeDetectorRef,
    public calendarService: CurrencyPairCalendarPrimaryLookupService,
  ) {}

  get format() {
    if (this.isMoment) return "DD-MMM-YY";

    return "DD-MMM-YYYY";
  }
  get value() {
    return this._value;
  }

  set value(val) {
    if (val !== undefined && this._value !== val) {
      this._value = val;
      this.textValue = val instanceof Date ? CubiqTS.moment(val).format(this.format) : "-";
      this.onChange(val);
      this.onTouch(val);
    }
    this.cd.markForCheck();
  }

  writeValue(value: any): void {
    if (this.calendar != null) {
      this._value = value;
      this.textValue = value instanceof Date ? CubiqTS.moment(value).format(this.format) : "-";
    } else {
      this.pendingValue = value;
    }
  }

  registerOnChange(fn: any): void {
    if (this.calendar != null) this.calendar.registerOnChange(fn);
    this.onChange = (...args) => {
      logger.out.info(`onChange %O`, args);
      fn(args);
    };
  }

  registerOnTouched(fn: any): void {
    if (this.calendar != null) this.calendar.registerOnTouched(fn);

    this.onTouch = (...args) => {
      logger.out.info(`onTouch %O`, args);
      fn(args);
    };
  }

  getValue() {
    if (this.isMoment) {
      if (this.value instanceof Date) {
        return CubiqTS.moment.utc([
          this.value.getFullYear(),
          this.value.getMonth(),
          this.value.getDate(),
        ]);
      } else {
        return CubiqTS.moment(this.value);
      }
    }
    return this.value;
  }

  async agInit(params: ICellEditorParams): Promise<void> {
    if (this.calendar != null && this.calendar != null && this.calendar instanceof Calendar) {
      this.params = params;

      if (params.value != null && params.value instanceof CubiqTS.StandardDateImpl) {
        let date = new Date(0);
        date.setFullYear(params.value.year);
        date.setMonth(params.value.month - 1);
        date.setDate(params.value.day);
        params.value = date;
      } else if (params.data.valueDateRequested && CubiqTS.moment.isMoment(params.data.valueDate)) {
        this.isMoment = true;
        params.value = params.data.valueDate.toDate();
        this.value = params.value;
      } else if (CubiqTS.moment.isMoment(params.value)) {
        this.isMoment = true;
        params.value = params.value.toDate();
        this.value = params.value;
      }

      await this.updateCalendar(params);
      this.writeValue(params.value);
      this.cd.markForCheck();

      this.focusElement.nativeElement.onblur = (event) => {
        setImmediate(() => {
          this.calendar.onInputBlur(event);
        });
      };

      if (params.context != null && params.context.isCellEditingEnabled) {
        this.editingEnabled = params.context.isCellEditingEnabled(params);
      }
      if (params.context != null && params.context.minDate) {
        this.minDateSubscription = params.context.minDate.subscribe((minDate) => {
          this.minDate = new Date(minDate);
        });
      }
      if (params.context != null && params.context.dfCalendarHolidays) {
        this.calendar.disabledDates = params.context.dfCalendarHolidays();
      }
      if (params.context != null && params.context.editingStoppedSubject) {
        this.onEditingStoppedSubscription = params.context.editingStoppedSubject.subscribe(() => {
          this.calendar.hideOverlay();
          params.stopEditing();
        });
      }
      this.onCloseSubscription = this.calendar.onClose.subscribe(() => {
        params.context.editingStoppedSubject.next();
      });

      setTimeout(() => {
        this.calendar.showOverlay();
        this.calendar.cd.markForCheck();
        this.focusElement.nativeElement.focus();
      }, 4);
    } else {
      this.pendingParams = params;
      this.cd.markForCheck();
    }
  }

  ngAfterViewInit(): void {
    this.ready = true;

    if (this.pendingParams != null) {
      const params = this.pendingParams;
      this.pendingParams = null;
      this.agInit(params);
    } else if (this.pendingValue != null) {
      const value = this.pendingValue;
      this.pendingValue = null;
      this.writeValue(value);
    }
    this.cd.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.onCloseSubscription != null) {
      this.onCloseSubscription.unsubscribe();
      this.onCloseSubscription = null;
    }
    if (this.minDateSubscription != null) {
      this.minDateSubscription.unsubscribe();
      this.minDateSubscription = null;
    }
    this.calendar.hideOverlay();
  }

  onModelChange(value): void {
    this.onSelect(value);
  }

  onSelect(value): void {
    this.value = value;
    if (this.params.context != null && this.params.context.dfCalendarNullFields instanceof Set) {
      for (let field of this.params.context.dfCalendarNullFields) {
        this.params.data[field] = null;
      }
    }
    if (typeof this.params.data.valueDateRequested === "boolean") {
      this.params.value = this.getValue();
      if (CubiqTS.moment(this.getValue()).isSame(this.params.data.lockedValueDate, "day")) {
        this.params.data.valueDate = null;
        this.params.data.valueDateRequested = false;
        this.params.data.valueDateInvalid = false;
      } else {
        this.params.data.valueDate = this.getValue();
        this.params.data.valueDateRequested = true;
        this.params.data.valueDateInvalid = false;
      }
    } else {
      this.params.value = CubiqTS.moment(this.getValue()).toDate();
      this.params.data[this.params.colDef.field] = this.getValue();
    }
    this.params.api.redrawRows();
    this.params.stopEditing();
    if (
      this.params.context != null &&
      this.params.context.editingStoppedSubject instanceof Subject
    ) {
      this.params.context.editingStoppedSubject.next();
    }
  }

  isCancelBeforeStart?(): boolean {
    return !this.params.context.isCellEditingEnabled(this.params);
  }

  isCancelAfterEnd?(): boolean {
    return !this.editingEnabled;
  }

  private async updateCalendar(params: ICellEditorParams) {
    if (this.isMoment) {
      // DF for now
      let ccyPairCal: CubiqTS.CurrencyPairCalendar =
        await this.calendarService.getCalendarWithHolidays(
          params.data.baseCode,
          params.data.nonBaseCode,
          params.data.product,
        );

      if (Array.isArray(ccyPairCal?.holidays)) {
        let disabledDates = ccyPairCal.holidays.map((a) => {
          return new Date(a.date.year, a.date.month - 1, a.date.day);
        });
        this.calendar.disabledDates = disabledDates;
      }

      this.lockedValueDate = params?.data?.lockedValueDate || null;

      // highlight default valueDate
      if (this.calendar["__df_calendar_orig_isToday"] == null) {
        this.calendar["__df_calendar_orig_isToday"] = this.calendar.isToday;

        this.calendar.isToday = (val: any, day: any, month: any, year: any) => {
          if (CubiqTS.moment.isMoment(this.lockedValueDate)) {
            const utcDay = CubiqTS.moment.tz([year, month, day], "UTC");
            return this.lockedValueDate.isSame(utcDay, "day");
          }

          return this.calendar["__df_calendar_orig_isToday"](val, day, month, year);
        };
      }

      this.calendar.value = this.params.value;
      this.calendar.updateUI();
    }
  }
}
